import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  Center,
  HStack,
  Icon,
  Tooltip,
  SimpleGrid,
  Flex,
} from "@chakra-ui/react";
import Card from "../../../../components/card/Card";
import { campaignService } from "../../../../services/campaignService";
import { MdInfo, MdTrendingUp, MdTrendingDown } from "react-icons/md";
import {
  formatCampaignDiscount,
  formatCampaignTypeLabel,
  getCampaignType,
} from "../../../../utils/campaignDisplayUtils";

export default function CampaignComparison() {
  const [loading, setLoading] = useState(true);
  const [performanceData, setPerformanceData] = useState(null);
  
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const bgHover = useColorModeValue("gray.50", "navy.700");
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await campaignService.getCampaignPerformance();
        
        if (response.success) {
          setPerformanceData(response.data);
        }
      } catch (error) {
        console.error('Kampanya performans verileri getirme hatası:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  if (loading) {
    return (
      <Card h="100%">
        <Text color={textColor} fontSize="lg" fontWeight="700" mb="4">
          Kampanya Karşılaştırması
        </Text>
        <Center py={8}>
          <Spinner color="brand.500" />
        </Center>
      </Card>
    );
  }
  
  if (!performanceData || !performanceData.campaigns || performanceData.campaigns.length === 0) {
    return (
      <Card h="100%">
        <Text color={textColor} fontSize="lg" fontWeight="700" mb="4">
          Kampanya Karşılaştırması
        </Text>
        <Center py={8}>
          <Text color="gray.500">Henüz kampanya verisi bulunmuyor</Text>
        </Center>
      </Card>
    );
  }
  
  // Kampanyaları kullanım sayısına göre sırala
  const sortedCampaigns = [...performanceData.campaigns].sort((a, b) => b.usageCount - a.usageCount);
  
  return (
    <Card h="100%">
      <Flex direction="column" h="100%" justify="space-between">
        <HStack spacing={2} mb="4">
          <Text color={textColor} fontSize="lg" fontWeight="700">
            Kampanya Karşılaştırması
          </Text>
          <Tooltip 
            label="Tüm kampanyaların performans metriklerini karşılaştırın" 
            placement="top"
            hasArrow
            bg="gray.700"
            color="white"
            fontSize="sm"
            openDelay={300}
          >
            <Box display="inline-block">
              <Icon as={MdInfo} w="16px" h="16px" color="gray.400" cursor="pointer" />
            </Box>
          </Tooltip>
        </HStack>
      
      <Box overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th borderColor={borderColor} color={textColor}>Kampanya</Th>
              <Th borderColor={borderColor} color={textColor} isNumeric>Görüntülenme</Th>
              <Th borderColor={borderColor} color={textColor} isNumeric>Kullanım</Th>
              <Th borderColor={borderColor} color={textColor} isNumeric>Gelir</Th>
              <Th borderColor={borderColor} color={textColor} isNumeric>İndirim</Th>
              <Th borderColor={borderColor} color={textColor} isNumeric>ROI</Th>
              <Th borderColor={borderColor} color={textColor}>Durum</Th>
            </Tr>
          </Thead>
          <Tbody>
            {sortedCampaigns.map((campaign) => (
              <Tr 
                key={campaign.campaignId} 
                _hover={{ bg: bgHover }}
                transition="all 0.2s"
              >
                <Td borderColor={borderColor}>
                  <Box>
                    <Text fontWeight="600" color={textColor} fontSize="sm">
                      {campaign.name}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {formatCampaignTypeLabel(campaign)}
                      {getCampaignType(campaign) === 'coupon' && campaign.code
                        ? ` · ${campaign.code}`
                        : ''}
                      {' · '}
                      {formatCampaignDiscount(campaign)}
                    </Text>
                  </Box>
                </Td>
                <Td borderColor={borderColor} isNumeric>
                  <Text fontSize="sm" color={textColor}>
                    {campaign.views.toLocaleString()}
                  </Text>
                </Td>
                <Td borderColor={borderColor} isNumeric>
                  <HStack justify="flex-end" spacing={1}>
                    <Text fontSize="sm" fontWeight="600" color={textColor}>
                      {campaign.usageCount}
                    </Text>
                    {campaign.usageCount > 0 && (
                      <Icon 
                        as={MdTrendingUp} 
                        color="green.400" 
                        boxSize={4} 
                      />
                    )}
                  </HStack>
                </Td>
                <Td borderColor={borderColor} isNumeric>
                  <Text fontSize="sm" color={textColor} fontWeight="600">
                    ₺{campaign.totalRevenue.toLocaleString('tr-TR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </Text>
                </Td>
                <Td borderColor={borderColor} isNumeric>
                  <Text fontSize="sm" color="red.400">
                    -₺{campaign.totalDiscountAmount.toLocaleString('tr-TR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </Text>
                </Td>
                <Td borderColor={borderColor} isNumeric>
                  <Badge 
                    colorScheme={campaign.roi > 10 ? "green" : campaign.roi > 5 ? "yellow" : "gray"}
                    fontSize="xs"
                  >
                    %{campaign.roi.toFixed(2)}
                  </Badge>
                </Td>
                <Td borderColor={borderColor}>
                  <Badge 
                    colorScheme={campaign.isActive ? "green" : "gray"}
                    fontSize="xs"
                  >
                    {campaign.isActive ? "Aktif" : "Pasif"}
                  </Badge>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      
      {performanceData.summary && (
        <Box mt={3} pt={3} borderTop="1px" borderColor={borderColor}>
          <SimpleGrid columns={3} spacing={2} fontSize="xs">
            <Box>
              <Text color="gray.500">Toplam:</Text>
              <Text fontWeight="600" color={textColor}>{performanceData.summary.totalCampaigns}</Text>
            </Box>
            <Box>
              <Text color="gray.500">Kullanım:</Text>
              <Text fontWeight="600" color={textColor}>{performanceData.summary.totalUsage}</Text>
            </Box>
            <Box>
              <Text color="gray.500">Gelir:</Text>
              <Text fontWeight="600" color={textColor}>
                ₺{performanceData.summary.totalRevenue.toLocaleString('tr-TR', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                })}
              </Text>
            </Box>
          </SimpleGrid>
        </Box>
      )}
      </Flex>
    </Card>
  );
}

